import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import InsufficientLoyaltyPointsCard from 'loyalty/components/InsufficientLoyaltyPointsCard'
import useOpenCharterFareClassModal from 'hooks/OfferPage/useOpenCharterFareClassModal'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import type { FareClassWithReward } from 'constants/flight'

interface OwnProps {
  rewardPointsDeficit: number
}

interface MappedProps {
  offer?: App.Offer
  fareClass: FareClassWithReward
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
}

type Props = OwnProps & MappedProps

/**
 * Card shown when the member has a rewards fare
 * selected but not enough loyalty/societe points to redeem it.
 */
function FlashHotelInsufficientLoyaltyPointsCard({ rewardPointsDeficit, offer, fareClass, checkInDate, checkOutDate, occupancies }: Props) {
  const intl = useIntl()
  const { canOpen, open } = useOpenCharterFareClassModal({ offer, fareClass, checkInDate, checkOutDate, occupancies })
  const { setRewardSeatsOnly } = useCharterFilterUrlState()

  // "View other options" exists to let the user escape the reward filter when they don't
  // have enough points — flip `?societeRewards=` to false AND pass an explicit override into
  // `open(...)` so the modal mounts with the toggle off, even though the parent's
  // `rewardSeatsOnly` closure is still stale at this synchronous call site.
  const handleViewOtherOptionsClick = useCallback(() => {
    setRewardSeatsOnly(false)
    open({ rewardSeatsOnly: false })
  }, [setRewardSeatsOnly, open])

  const secondaryAction = canOpen ? (
    <>
      <Caption variant="medium" colour="neutral-two">
        {intl.formatMessage({
          id: 'Chrk1O53GPR6pKXkK7j1',
          defaultMessage: 'or',
          description: 'Separator between the primary and secondary CTAs on the insufficient-points card on the Flash hotel offer page.',
        })}
      </Caption>
      <TextButton kind="secondary" fit="full-width" onClick={handleViewOtherOptionsClick} size="small">
        {intl.formatMessage({
          id: '2LeFYRhZ7kg-G7Zqi5ME',
          defaultMessage: 'View other options',
          description: 'Secondary CTA on the insufficient-points card on the Flash hotel offer page; opens the fare-class selection modal so members can pick a non-reward fare.',
        })}
      </TextButton>
    </>
  ) : undefined

  return (
    <InsufficientLoyaltyPointsCard
      rewardPointsDeficit={rewardPointsDeficit}
      secondaryAction={secondaryAction}
    />
  )
}

export default withOfferPageState((state): MappedProps => ({
  offer: state.offer,
  fareClass: state.fareClass,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  occupancies: state.rooms,
}), FlashHotelInsufficientLoyaltyPointsCard)
